import React, { useEffect, useRef } from 'react';
import { Icon } from './components/Icon';

const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Element, który dostaje fokus po otwarciu dialogu (audyt 2026-09-30): jawny `autofocus` /
 * `data-autofocus` → pierwsze pole formularza → dowolny focusowalny element. Wcześniej wygrywał
 * PIERWSZY focusowalny = „Zamknij ×" z paska tytułu — kto zaczynał pisać, pisał w przycisk,
 * a Spacja/Enter zamykały modal.
 */
function initialFocusTarget(panel: HTMLElement): HTMLElement | null {
  return (
    panel.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ??
    panel.querySelector<HTMLElement>(FIELD_SELECTOR) ??
    panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
  );
}

export interface ModalShellProps {
  /** Whether the dialog is mounted/visible. */
  open: boolean;
  /** Optional dialog heading rendered in the top bar. */
  title?: React.ReactNode;
  /** Called on scrim click, ESC, or the close button. */
  onClose: () => void;
  /** Hide the top-right close button (e.g. when the title bar is omitted). Default false. */
  hideClose?: boolean;
  /** Dialog body. */
  children: React.ReactNode;
  /** Optional footer area (actions). */
  footer?: React.ReactNode;
  /** Max width of the panel. Default 560px. */
  width?: number | string;
  /** Accessible label when no visible title is given. */
  ariaLabel?: string;
}

/**
 * ModalShell — the shared dialog container for the web admin.
 * Renders a scrim + a flat DS surface panel (radius-lg) with an optional
 * title bar + DS close icon (x). Handles ESC, scrim-click and focus capture.
 * ConfirmModal and any custom dialogs compose on top of this.
 */
export function ModalShell({
  open,
  title,
  onClose,
  hideClose = false,
  children,
  footer,
  width = 560,
  ariaLabel,
}: ModalShellProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // 2026-09-15 (właściciel): „można wpisać jedną literkę i input się zamyka" — w „Dane właściciela /
  // zarządcy" i „Dodaj budynek". Przyczyna: efekt niżej zależał od `onClose`, a wywołujący podają
  // inline `() => setOpen(false)` (nowa funkcja co render). Każda litera → setState rodzica →
  // nowy `onClose` → cleanup oddawał fokus przyciskowi sprzed otwarcia, a efekt przenosił go na
  // PIERWSZY focusowalny element panelu (ikona „×"). Handler trzymamy w refie: efekt zależy tylko od `open`.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Element do przywrócenia fokusu po zamknięciu łapiemy w RENDERZE otwarcia (panel jeszcze
  // niezamontowany): React wykonuje `autoFocus` dzieci w fazie commit, PRZED efektami — w efekcie
  // `document.activeElement` byłby już polem z wnętrza dialogu, a nie przyciskiem, który go otworzył.
  if (open && !panelRef.current && typeof document !== 'undefined') {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
  }

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    // Fokus do dialogu — chyba że już tam jest (React `autoFocus` pola, np. „Ulica" w „Dodaj
    // budynek", ustawił go w commicie); wtedy go NIE przestawiamy.
    const node = panelRef.current;
    if (node && !node.contains(document.activeElement)) {
      (initialFocusTarget(node) ?? node).focus();
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const showTitleBar = title != null || !hideClose;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-5)',
        background: 'rgba(20, 24, 26, .32)',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title == null ? ariaLabel : undefined}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: typeof width === 'number' ? `${width}px` : width,
          maxHeight: 'calc(100vh - var(--space-8))',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface-panel)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-float)',
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        {showTitleBar && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-4)',
              flex: 'none',
              padding: 'var(--space-5) var(--space-6)',
              borderBottom: title != null ? 'var(--border-width) solid var(--border)' : 'none',
            }}
          >
            {title != null ? (
              <h2
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-h2)',
                  fontWeight: 'var(--weight-bold)',
                  lineHeight: 'var(--leading-tight)',
                  color: 'var(--text-heading)',
                }}
              >
                {title}
              </h2>
            ) : (
              <span />
            )}
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Zamknij"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  flex: 'none',
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--surface-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon name="x" size={24} />
              </button>
            )}
          </div>
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: 'var(--space-6)',
            overflowY: 'auto',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {children}
        </div>

        {footer != null && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)',
              flex: 'none',
              padding: 'var(--space-5) var(--space-6)',
              borderTop: 'var(--border-width) solid var(--border)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
